<template>
  <v-dialog v-model="isOpen" max-width="600" scrollable persistent>
    <v-card class="comment-modal">
      <!-- Header -->
      <v-card-title class="comment-modal__header">
        <v-btn
          icon
          size="small"
          variant="text"
          class="comment-modal__close"
          @click="close"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
        <h2 class="comment-modal__title">Comments</h2>
        <div class="comment-modal__spacer" />
      </v-card-title>

      <v-card-text class="comment-modal__content">
        <!-- Original Post Preview -->
        <div v-if="post" class="comment-modal__post">
          <div class="comment-modal__post-avatar">
            <img
              v-if="postUserAvatar"
              :src="postUserAvatar"
              :alt="postUserName"
            />
            <v-icon v-else size="20">mdi-account</v-icon>
          </div>
          <div class="comment-modal__post-content">
            <div class="comment-modal__post-header">
              <span class="comment-modal__post-name">{{ postUserName }}</span>
              <span class="comment-modal__post-username"
                >@{{ postUserUsername }}</span
              >
            </div>
            <p class="comment-modal__post-body">
              {{ post.content || post.title || "" }}
            </p>
          </div>
        </div>

        <!-- Comment Input - Authenticated users -->
        <div v-if="isAuthenticated" class="comment-modal__input-container">
          <div class="comment-modal__input-wrapper">
            <RichTextEditor
              ref="editorRef"
              v-model="editorContent"
              placeholder="Add a comment... Use @ to mention users"
              :max-length="maxLength"
              :compact="true"
              :show-toolbar="showToolbar"
              :disabled="isSubmitting"
              :enable-media="false"
              :enable-emoji="true"
              :submit-on-cmd-enter="true"
              class="comment-modal__editor"
              @submit="submitComment"
            />
            <div v-if="attachedImages.length > 0" class="comment-modal__attachments">
              <div v-for="(img, idx) in attachedImages" :key="idx" class="comment-modal__attachment-item">
                <img :src="img.url" alt="Attached" />
                <button class="comment-modal__attachment-remove" type="button" @click="removeAttachment(idx)">
                  <v-icon size="14">mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div class="comment-modal__input-footer">
              <v-btn
                icon
                size="x-small"
                variant="text"
                :color="showToolbar ? 'primary' : undefined"
                class="comment-modal__format-toggle"
                @click="showToolbar = !showToolbar"
              >
                <v-icon size="18">mdi-format-text</v-icon>
              </v-btn>
              <input ref="fileInputRef" type="file" accept="image/*" multiple hidden @change="handleFileSelect" />
              <v-btn icon size="x-small" variant="text" :disabled="isSubmitting || attachedImages.length >= 4" @click="fileInputRef?.click()">
                <v-icon size="18">mdi-image-plus</v-icon>
              </v-btn>
              <span
                v-if="isOverLimit"
                class="comment-modal__char-error"
                role="alert"
              >
                {{ characterCount - maxLength }} characters over limit
              </span>
              <div class="comment-modal__footer-spacer" />
              <v-btn
                color="primary"
                size="small"
                :disabled="!canSubmit"
                :loading="isSubmitting"
                @click="submitComment"
              >
                Comment
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Login prompt - Unauthenticated users -->
        <div v-else class="comment-modal__login-prompt">
          <v-icon size="20" class="comment-modal__login-icon">mdi-account-outline</v-icon>
          <span class="comment-modal__login-text">Want to join the conversation?</span>
          <v-btn
            color="primary"
            size="small"
            variant="elevated"
            @click="router.push({ path: '/login', query: { redirect: router.currentRoute.value.fullPath } })"
          >
            Login to comment
          </v-btn>
        </div>

        <!-- Comments List -->
        <div class="comment-modal__list">
          <!-- Loading -->
          <div v-if="isLoading" class="comment-modal__loading">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>

          <!-- Empty State -->
          <div v-else-if="comments.length === 0" class="comment-modal__empty">
            <v-icon size="48" color="surface-variant"
              >mdi-comment-outline</v-icon
            >
            <p>No comments yet</p>
            <span>Be the first to comment!</span>
          </div>

          <!-- Comments -->
          <template v-else>
            <CommentItem
              v-for="comment in comments"
              :key="comment.ulid"
              :comment="comment"
              @reaction-change="handleReactionChange"
              @reply="handleReplyToComment"
              @delete="confirmDeleteComment"
            />

            <!-- Load More -->
            <div v-if="hasMore" class="comment-modal__load-more">
              <v-btn
                variant="text"
                color="primary"
                size="small"
                :loading="isLoadingMore"
                @click="loadMore"
              >
                Load more comments
              </v-btn>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteConfirm" max-width="400">
    <v-card class="delete-dialog">
      <v-card-title class="delete-dialog__title">
        Delete comment?
      </v-card-title>
      <v-card-text class="delete-dialog__text">
        This action cannot be undone.
      </v-card-text>
      <v-card-actions class="delete-dialog__actions">
        <v-spacer />
        <v-btn variant="text" @click="cancelDelete"> Cancel </v-btn>
        <v-btn
          color="error"
          variant="flat"
          :loading="isDeleting"
          @click="deleteComment"
        >
          Delete
        </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ActionFeedbackOverlay
      :show="feedback.show"
      :icon="feedback.icon"
      :text="feedback.text"
      :type="feedback.type"
    />
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useSdk } from "@/composables/useSdk";
import { useAuth } from "@/composables/useAuth";
import { usePusher } from "@/composables/usePusher";
import { useActionFeedback } from "@/composables/useActionFeedback";
import ActionFeedbackOverlay from "@/components/feedback/ActionFeedbackOverlay.vue";
import CommentItem from "./CommentItem.vue";
import { RichTextEditor } from "@/components/editor";
import type { Post, Comment } from "@/types";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";
import type { JSONContent } from "@tiptap/vue-3";

// Type for editor ref - matches RichTextEditor.vue defineExpose
interface RichTextEditorInstance {
  editor: unknown;
  focus: () => void;
  clear: () => void;
  isEmpty: () => boolean;
  getText: () => string;
  getHTML: () => string;
  getJSON: () => JSONContent | undefined;
  characterCount: { value: number }; // ComputedRef<number>
}

const router = useRouter();
const { sdk } = useSdk();
const { user: currentUser, isAuthenticated } = useAuth();
const { subscribeToPostComments, unsubscribe } = usePusher();
const { feedback, showFeedback } = useActionFeedback();

// State
const isOpen = ref(false);
const post = ref<Post | null>(null);
const comments = ref<Comment[]>([]);
const editorContent = ref<JSONContent>({ type: "doc", content: [] });
const editorRef = ref<RichTextEditorInstance | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const attachedImages = ref<{ url: string; file: File }[]>([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const isSubmitting = ref(false);
const hasMore = ref(false);
const cursor = ref<string | null>(null);
const maxLength = 500;
const showToolbar = ref(false);

// Delete state
const showDeleteConfirm = ref(false);
const commentToDelete = ref<Comment | null>(null);
const isDeleting = ref(false);

// Auto-close timeout (cleared if user manually closes or starts typing)
let closeTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Reaction state - ReactionPicker handles loading internally

// User profile state
const cachedUserProfile = ref<UserProfile | null>(null);

// Helper to get user ID from post
const postUserId = computed(() => {
  if (!post.value) return null;
  const p = post.value as Post & {
    userId?: string;
    user?: { ulid?: string; userId?: string };
  };
  return p.userId || p.user?.ulid || p.user?.userId || null;
});

// Fetch user profile
async function fetchUserProfile() {
  const userId = postUserId.value;
  if (!userId || !sdk) return;

  try {
    const profile = await sdk.fetchUserProfileById(userId);
    if (profile) cachedUserProfile.value = profile;
  } catch (err) {
    console.error("Failed to fetch user profile:", err);
  }
}

// Computed properties using cached profile
const postUserAvatar = computed(() => {
  if (cachedUserProfile.value?.avatarUrl) {
    return cachedUserProfile.value.avatarUrl;
  }
  const user = (post.value as Post & { user?: { avatar?: string } })?.user;
  return user?.avatar || null;
});

const postUserName = computed((): string => {
  if (cachedUserProfile.value) {
    const name = (cachedUserProfile.value as Record<string, unknown>).name;
    return (
      (typeof name === "string" ? name : "") ||
      cachedUserProfile.value.displayName ||
      "Unknown"
    );
  }
  const p = post.value as Post & {
    user?: { name?: string; displayName?: string };
  };
  return p?.user?.name || p?.user?.displayName || "Unknown";
});

const postUserUsername = computed(() => {
  if (cachedUserProfile.value?.username) {
    return cachedUserProfile.value.username;
  }
  const p = post.value as Post & { user?: { username?: string } };
  return p?.user?.username || "unknown";
});

// Character count from editor
const characterCount = computed(() => editorRef.value?.characterCount?.value ?? 0);
const isOverLimit = computed(() => characterCount.value > maxLength);

// Track editorContent for reactivity (isEmpty() alone doesn't trigger updates)
const canSubmit = computed(() => {
  const hasContent = (editorContent.value?.content?.length ?? 0) > 0;
  const isEmpty = editorRef.value?.isEmpty() ?? true;
  const hasAttachments = attachedImages.value.length > 0;
  return ((!isEmpty && hasContent) || hasAttachments) && !isOverLimit.value && !isSubmitting.value;
});

// Handle real-time comment added events from Pusher
function handleCommentAdded(commentData: any) {
  try {
    console.log("[CommentModal] Received comment.added event:", commentData);
    
    // PostResource data comes directly from broadcastWith()
    // The data structure is the PostResource array directly
    const comment = commentData as Comment;
    
    // Check if comment already exists (avoid duplicates)
    if (!comment?.ulid) {
      console.warn("[CommentModal] Received comment event without ulid:", commentData);
      return;
    }

    const exists = comments.value.some((c) => c.ulid === comment.ulid);
    if (exists) {
      console.log("[CommentModal] Comment already exists, skipping:", comment.ulid);
      return;
    }

    // Ensure userId is set - PostResource includes userId at top level and in user.userId
    // PostResource user object has userId, but Comment type user has ulid
    const userData = comment.user as any;
    const enrichedComment: Comment = {
      ...comment,
      userId: comment.userId || userData?.userId || userData?.ulid || comment.user?.ulid,
    };

    console.log("[CommentModal] Adding comment to list:", enrichedComment.ulid, "userId:", enrichedComment.userId);
    comments.value.unshift(enrichedComment);
  } catch (err) {
    console.error("[CommentModal] Error handling comment added event:", err, commentData);
  }
}

async function open(targetPost: Post) {
  post.value = targetPost;
  isOpen.value = true;

  // Clear and fetch user profile
  cachedUserProfile.value = null;
  fetchUserProfile();

  // Load comments
  await loadComments();

  // Subscribe to real-time comment updates for this post
  if (targetPost.ulid) {
    subscribeToPostComments(targetPost.ulid, handleCommentAdded);
  }
}

function close() {
  // Clear any pending auto-close timeout
  if (closeTimeoutId) {
    clearTimeout(closeTimeoutId);
    closeTimeoutId = null;
  }

  // Unsubscribe from post channel
  if (post.value?.ulid) {
    unsubscribe(`post.${post.value.ulid}`);
  }

  isOpen.value = false;
  post.value = null;
  comments.value = [];
  editorRef.value?.clear();
  editorContent.value = { type: "doc", content: [] };
  cursor.value = null;
  hasMore.value = false;
}

async function loadComments() {
  if (!post.value?.ulid) return;

  isLoading.value = true;
  try {
    const result = await sdk.getPostComments(post.value.ulid);
    comments.value = (result.posts || []) as unknown as Comment[];
    cursor.value = result.nextCursor || null;
    hasMore.value = Boolean(result.nextCursor);
  } catch (err) {
    console.error("Failed to load comments:", err);
    showFeedback("mdi-alert-circle-outline", "Load failed", "error");
  } finally {
    isLoading.value = false;
  }
}

async function loadMore() {
  if (!post.value?.ulid || !cursor.value || isLoadingMore.value) return;

  isLoadingMore.value = true;
  try {
    const result = await sdk.getPostComments(post.value.ulid, cursor.value);
    comments.value.push(...(result.posts as unknown as Comment[]));
    cursor.value = result.nextCursor || null;
    hasMore.value = Boolean(result.nextCursor);
  } catch (err) {
    console.error("Failed to load more comments:", err);
    showFeedback("mdi-alert-circle-outline", "Load failed", "error");
  } finally {
    isLoadingMore.value = false;
  }
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  if (!input.files) return;
  const remaining = 4 - attachedImages.value.length;
  for (let i = 0; i < Math.min(input.files.length, remaining); i++) {
    const file = input.files[i];
    if (file.type.startsWith("image/")) {
      attachedImages.value.push({ url: URL.createObjectURL(file), file });
    }
  }
  input.value = "";
}

function removeAttachment(index: number) {
  const removed = attachedImages.value.splice(index, 1)[0];
  if (removed) URL.revokeObjectURL(removed.url);
}

async function submitComment() {
  if (!isAuthenticated.value) {
    router.push({ path: "/login", query: { redirect: router.currentRoute.value.fullPath } });
    return;
  }

  if (!canSubmit.value || !post.value?.ulid) return;

  const commentBody = editorRef.value?.getHTML() || "";
  const hasText = commentBody.trim().length > 0;
  const hasAttachments = attachedImages.value.length > 0;
  if (!hasText && !hasAttachments) return;

  isSubmitting.value = true;
  try {
    const imageUrls: string[] = [];
    const userUlid = currentUser.value?.ulid;
    if (hasAttachments && userUlid) {
      for (const img of attachedImages.value) {
        const result = await sdk.uploadMediaFile(img.file, { userUlid, mediaType: "image" });
        imageUrls.push(result.url);
      }
    }

    const newComment = await sdk.createComment({
      parentId: post.value.ulid,
      body: commentBody,
      images: imageUrls.length > 0 ? imageUrls : undefined,
    });

    const optimisticImages = imageUrls.map((url) => ({ url }));
    const enrichedComment = {
      ...newComment,
      userId: currentUser.value?.ulid || newComment.userId,
      user: currentUser.value
        ? {
            userId: currentUser.value.ulid,
            ulid: currentUser.value.ulid,
            username: currentUser.value.username,
            name: currentUser.value.name,
            avatar: currentUser.value.avatar || currentUser.value.avatarUrl,
          }
        : newComment.user,
      images: optimisticImages.length > 0 ? optimisticImages : newComment.images,
    } as unknown as Comment;

    comments.value.unshift(enrichedComment);
    editorRef.value?.clear();
    editorContent.value = { type: "doc", content: [] };
    attachedImages.value.forEach((img) => URL.revokeObjectURL(img.url));
    attachedImages.value = [];
    showFeedback("mdi-check-circle-outline", "Posted", "success");

    // Close modal after brief delay so user sees feedback
    closeTimeoutId = setTimeout(() => close(), 800);
  } catch (err) {
    console.error("Failed to post comment:", err);
    showFeedback("mdi-alert-circle-outline", "Post failed", "error");
  } finally {
    isSubmitting.value = false;
  }
}

function handleReactionChange(comment: Comment, reaction: string | null) {
  // Optimistically update the comment's reaction data
  // ReactionPicker handles the API calls, we just update local state
  if (!comment.postEngagement) {
    comment.postEngagement = {
      reactionCounts: {},
      userReaction: null,
    };
  }

  const previousReaction = comment.postEngagement.userReaction;
  comment.postEngagement.userReaction = reaction;

  // Update reaction counts optimistically
  if (!comment.postEngagement.reactionCounts) {
    comment.postEngagement.reactionCounts = {};
  }

  // Decrease previous reaction count
  if (previousReaction && comment.postEngagement.reactionCounts[previousReaction]) {
    comment.postEngagement.reactionCounts[previousReaction] = Math.max(
      0,
      (comment.postEngagement.reactionCounts[previousReaction] || 0) - 1
    );
  }

  // Increase new reaction count
  if (reaction) {
    comment.postEngagement.reactionCounts[reaction] =
      (comment.postEngagement.reactionCounts[reaction] || 0) + 1;
  }
}

async function handleReplyToComment(comment: Comment) {
  // Resolve username and userId from comment or SDK cache
  let username = comment.user?.username;
  let userId = comment.userId || comment.user?.ulid;

  if ((!username || !userId) && comment.userId && sdk) {
    try {
      const profile = await sdk.fetchUserProfileById(comment.userId);
      username = profile?.username || username;
      userId = comment.userId;
    } catch (err) {
      console.error("Failed to fetch user profile for reply:", err);
    }
  }

  if (!username) return;

  await nextTick();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editor = editorRef.value?.editor as any;
  if (!editor) return;

  // Insert mention node (with userId) or fallback to plain text
  const content = userId
    ? { type: "mention", attrs: { id: userId, label: username } }
    : `@${username}`;

  editor.chain().focus().insertContent(content).insertContent(" ").run();
}

function confirmDeleteComment(comment: Comment) {
  commentToDelete.value = comment;
  showDeleteConfirm.value = true;
}

async function deleteComment() {
  if (!commentToDelete.value?.ulid || isDeleting.value) return;

  isDeleting.value = true;
  try {
    await sdk.deleteComment(commentToDelete.value.ulid);

    // Remove from list
    comments.value = comments.value.filter(
      (c) => c.ulid !== commentToDelete.value?.ulid,
    );

    showFeedback("mdi-delete-outline", "Deleted", "success");
    showDeleteConfirm.value = false;
    commentToDelete.value = null;
  } catch (err) {
    console.error("Failed to delete comment:", err);
    showFeedback("mdi-alert-circle-outline", "Delete failed", "error");
  } finally {
    isDeleting.value = false;
  }
}

function cancelDelete() {
  showDeleteConfirm.value = false;
  commentToDelete.value = null;
}

// Cancel auto-close if user starts typing another comment
watch(editorContent, (newContent) => {
  const hasContent = (newContent?.content?.length ?? 0) > 0;
  if (hasContent && closeTimeoutId) {
    clearTimeout(closeTimeoutId);
    closeTimeoutId = null;
  }
}, { deep: true });

// Watch for post changes to fetch user profile
watch(post, (newPost, oldPost) => {
  // Unsubscribe from old post channel
  if (oldPost?.ulid) {
    unsubscribe(`post.${oldPost.ulid}`);
  }

  // Subscribe to new post channel
  if (newPost?.ulid) {
    subscribeToPostComments(newPost.ulid, handleCommentAdded);
  }
  if (newPost) {
    cachedUserProfile.value = null;
    fetchUserProfile();
  }
});

// Expose methods
defineExpose({
  open,
  close,
  isOpen,
});
</script>

<style scoped lang="scss">
.comment-modal {
  :deep(.v-card-text) {
    padding: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    padding-top: calc(12px + var(--safe-area-top));
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  &__close {
    margin-right: 8px;
  }

  &__title {
    flex: 1;
    text-align: center;
    font-family: var(--font-display);
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0;
  }

  &__spacer {
    width: 36px;
  }

  &__content {
    max-height: 60vh;
    overflow-y: auto;
  }

  &__post {
    display: flex;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  &__post-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(var(--v-theme-on-surface), 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__post-content {
    flex: 1;
    min-width: 0;
  }

  &__post-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  &__post-name {
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    font-size: 0.9375rem;
  }

  &__post-username {
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
  }

  &__post-body {
    color: rgba(var(--v-theme-on-surface), 0.8);
    font-size: 0.9375rem;
    line-height: 1.5;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__input-container {
    display: flex;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  }

  &__input-wrapper {
    flex: 1;
    min-width: 0;
  }

  &__format-toggle {
    opacity: 0.6;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 1;
    }
  }

  &__editor {
    :deep(.rich-editor__content) {
      min-height: 60px;
      max-height: 150px;
    }
  }

  &__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  &__attachment-item {
    position: relative;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;

    img { width: 100%; height: 100%; object-fit: cover; }
  }

  &__attachment-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 22px;
    height: 22px;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover { background: rgba(0, 0, 0, 0.9); }
  }

  &__input-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
  }

  &__footer-spacer {
    flex: 1;
  }

  &__char-error {
    font-size: 0.75rem;
    font-weight: 500;
    color: rgb(var(--v-theme-error));
    white-space: nowrap;
  }

  &__char-count {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);

    &--over {
      color: rgb(var(--v-theme-error));
    }
  }

  &__login-prompt {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    background: rgba(var(--v-theme-on-surface), 0.02);

    @media (max-width: 400px) {
      flex-direction: column;
      text-align: center;
    }
  }

  &__login-icon {
    color: rgba(var(--v-theme-on-surface), 0.4);
  }

  &__login-text {
    flex: 1;
    font-size: 0.9375rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    min-width: 0;
  }

  &__list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }

  &__loading,
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    text-align: center;
  }

  &__empty {
    p {
      font-size: 1rem;
      color: rgba(var(--v-theme-on-surface), 0.6);
      margin: 12px 0 4px;
    }

    span {
      font-size: 0.875rem;
      color: rgba(var(--v-theme-on-surface), 0.5);
    }
  }

  &__load-more {
    display: flex;
    justify-content: center;
    padding: 12px 20px;
  }
}
</style>


